import {
  SESSION_RECORDING_CONSENT_REQUEST_PUSH_TYPE,
  SESSION_RECORDING_DRAFT_READY_PUSH_TYPE,
} from '@shared/sessions/session-recording'
import { recordingConsentRequestPath } from '@shared/sessions/session-notification-history'

type RecordingPushData = {
  type?: string
  sessionUuid?: string
  uuid?: string
  recordingId?: string
  requestId?: string
} | null | undefined

export function sessionRecordingReviewPath(sessionUuid: string, recordingId: string): string {
  return `/sessions/${encodeURIComponent(sessionUuid)}/recordings/${encodeURIComponent(recordingId)}`
}

/**
 * Tap on a session-recording push: "draft ready" → the review page (or the
 * session when the payload lacks a recording id); "asking for your consent"
 * → its accept screen (or Alerts). `null` for other pushes.
 */
export function resolveSessionRecordingPushRoute(data: RecordingPushData): string | null {
  if (!data) return null
  const sessionUuid = (data.sessionUuid ?? data.uuid ?? '').trim()
  if (data.type === SESSION_RECORDING_CONSENT_REQUEST_PUSH_TYPE) {
    const requestId = (data.requestId ?? '').trim()
    return sessionUuid && requestId ? recordingConsentRequestPath(sessionUuid, requestId) : '/notifications'
  }
  if (data.type !== SESSION_RECORDING_DRAFT_READY_PUSH_TYPE) return null
  const recordingId = (data.recordingId ?? '').trim()
  if (sessionUuid && recordingId) return sessionRecordingReviewPath(sessionUuid, recordingId)
  if (sessionUuid) return `/sessions?session=${encodeURIComponent(sessionUuid)}`
  return '/sessions'
}

/**
 * Android shows no tray notification while the app is open, so a consent
 * request arriving then gets the in-app banner (iOS presents the alert itself).
 */
export function resolveForegroundConsentRequestBanner(
  notification: { title?: string | null; body?: string | null; data?: Record<string, string> | null },
  options: { appForegrounded: boolean; platform: string },
): { title: string; body: string; route: string } | null {
  if (options.platform !== 'ANDROID' || !options.appForegrounded) return null
  const data = notification.data ?? {}
  if (data.type !== SESSION_RECORDING_CONSENT_REQUEST_PUSH_TYPE) return null
  const route = resolveSessionRecordingPushRoute(data)
  if (!route) return null
  return {
    title: (notification.title ?? '').trim() || 'Consent to record a session',
    body: (notification.body ?? '').trim(),
    route,
  }
}
